import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Seo } from "@/lib/seo";
import { EASE, viewportOnce } from "@/lib/motion";
import { IMAGES, PROJECT_CATEGORIES } from "@/lib/data";
import { Fade, Mask } from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";

export default function Realisations() {
  return (
    <main id="contenu">
      <Seo
        title="Réalisations — Études de cas en préparation | SONOCO EVENTS"
        description="Les études de cas des productions techniques de SONOCO EVENTS seront publiées prochainement : événementiel corporate, éclairage architectural, production scénique, installations techniques et scénographie."
        path="/realisations"
      />

      <header className="mx-auto max-w-[1600px] px-5 pb-14 pt-32 sm:px-10 sm:pb-20 sm:pt-44 lg:px-16">
        <SectionLabel num="03" name="Réalisations" />
        <h1 className="mt-10 text-[clamp(3rem,10vw,9rem)] font-extrabold uppercase leading-[0.85] tracking-tightest">
          <Mask>Nos réalisations.</Mask>
        </h1>
      </header>

      <section className="mx-auto max-w-[1600px] px-5 sm:px-10 lg:px-16">
        <Fade>
          <div className="relative overflow-hidden border border-line">
            <motion.img
              src={IMAGES.realisations}
              alt="Scène vide sous les projecteurs avant une production — SONOCO EVENTS"
              className="aspect-[16/9] w-full object-cover opacity-50 grayscale sm:aspect-[21/9]"
              initial={{ scale: 1.06 }}
              whileInView={{ scale: 1 }}
              viewport={viewportOnce}
              transition={{ duration: 1.6, ease: EASE }}
            />
            <div className="absolute inset-0 flex items-center justify-center bg-bg/40">
              <span className="border border-ink/25 px-6 py-3 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-ink sm:text-[11px]">
                Études de cas en préparation
              </span>
            </div>
          </div>
        </Fade>

        <div className="mt-16 grid gap-12 lg:grid-cols-12">
          <Fade className="lg:col-span-7" delay={0.1}>
            <p className="text-xl font-light leading-relaxed text-ink sm:text-2xl">
              Cette page est en préparation. Nous y publierons prochainement les études de cas de
              nos productions : contexte, contraintes techniques, solutions mises en œuvre et
              résultats.
            </p>
          </Fade>
          <Fade className="lg:col-span-4 lg:col-start-9" delay={0.2}>
            <p className="text-base leading-relaxed text-mute">
              En attendant, parlons directement de votre projet : nos équipes étudient chaque
              demande technique sur mesure.
            </p>
            <Link
              to="/contact"
              data-testid="realisations-cta-contact"
              className="group mt-6 inline-flex items-center gap-3 border border-ink/30 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] text-ink transition-all duration-300 hover:border-ink hover:bg-ink hover:text-bg"
            >
              Un projet en tête ?
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Fade>
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-5 py-24 sm:px-10 sm:py-36 lg:px-16">
        <SectionLabel num="01" name="Catégories couvertes" />
        <div className="mt-12">
          {PROJECT_CATEGORIES.map((cat, i) => (
            <Fade key={cat} delay={i * 0.05} y={16}>
              <div
                data-testid={`realisations-category-${i + 1}`}
                className="flex items-center justify-between gap-6 border-t border-line py-5 last:border-b"
              >
                <span className="font-mono text-xs text-mute">0{i + 1}</span>
                <span className="flex-1 text-xl font-bold uppercase tracking-tight text-ink/90 sm:text-3xl">
                  {cat}
                </span>
                <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-faint">
                  À venir
                </span>
              </div>
            </Fade>
          ))}
        </div>
      </section>
    </main>
  );
}
